import { useEffect, useState } from 'react'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import type { ShareLinkDoc } from '@racedash/shared'
import { db } from '../lib/firebase'

export interface ShareLink extends ShareLinkDoc {
  token: string
}

/**
 * Live share links for one dashboard. Editors only — the rules refuse this
 * collection to viewers, because the document id is the token and the matrix
 * reserves tokens to editors. Viewers see the hasActiveLink badge instead.
 *
 * The orgId filter is not a filter, it is what makes the query legal. The rule
 * is `allow list: if isEditor(resource.data.orgId)`, and Firestore will only
 * run a query it can prove in advance every document of will satisfy — a query
 * constrained on dashboardId alone could in principle reach another
 * organisation's link, so the whole query is refused, even when it would return
 * nothing. Constraining orgId is what makes it provable.
 */
export function useShareLinks(
  dashboardId: string | undefined,
  orgId: string | undefined,
  enabled: boolean,
) {
  const [links, setLinks] = useState<ShareLink[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!dashboardId || !orgId || !enabled) {
      setLinks(null)
      return
    }
    return onSnapshot(
      query(
        collection(db, 'shareLinks'),
        where('orgId', '==', orgId),
        where('dashboardId', '==', dashboardId),
      ),
      (snap) => {
        const live = snap.docs
          .map((d) => ({ token: d.id, ...(d.data() as ShareLinkDoc) }))
          .filter((l) => !l.revokedAt)
        setLinks(live)
      },
      (e) => setError(e.message),
    )
  }, [dashboardId, orgId, enabled])

  return { links, error }
}
